import React, {
  useEffect, useCallback, ReactNode,
} from 'react';
import {
  Button, Col, Modal, Row, Tag,
} from 'antd';
import { BarcodeOutlined, UserOutlined } from '@ant-design/icons';
import humps from 'lodash-humps-ts';
import { useTranslation } from 'react-i18next';
import JobStatusToast from '../../molecules/job-status-toast';
import { JobStatusProps, JobSituationDataProps } from './types';

const JobStatus = ({
  statusStr, archivedAt, stage, stageCount,
  expectedStaff, allowedStaff, activeStaff, user, jobSituations, openPopOver, loading,
  setOpenChangePopOver, id,
}: JobStatusProps & { jobSituations?: JobSituationDataProps }) => {
  const { t } = useTranslation();
  const [displayStatus, setDisplayStatus] = React.useState<(ReactNode)[]>([]);
  const displayDraftStatus = () => <Tag color="blue" className="m-0">{t('job.jobStatus.incomplete')}</Tag>;
  const displayArchivedStatus = () => <Tag color="red" className="m-0">{t('job.jobStatus.trashed')}</Tag>;
  const generateAcceptedJobStatus = useCallback(() => {
    let acceptedStatus = [
      <Tag key="accepted" color="green" className="m-0">
        A
        <small>
          x$
          {stage - 1}
        </small>
      </Tag>,
    ];

    if (allowedStaff !== null) {
      acceptedStatus = [...acceptedStatus, <Tag key="allowedStaff" color="blue" icon={<UserOutlined />} className="m-0"><small>{expectedStaff !== null ? `${allowedStaff}/${expectedStaff}` : allowedStaff}</small></Tag>];
    }

    if (activeStaff !== null) {
      acceptedStatus = [...acceptedStatus, <Tag key="activeStaff" color={activeStaff === 0 ? 'yellow' : 'grey'} icon={<BarcodeOutlined />} className="m-0">{activeStaff}</Tag>];
    }
    setDisplayStatus(acceptedStatus);
  }, [activeStaff, allowedStaff, expectedStaff, stage]);

  const generatePreSteps = useCallback(() => {
    let otherStatus: ReactNode[] = [];
    let index = 0;
    while (index < stage) {
      if (index === 0) {
        otherStatus = [<Tag key={index} color="blue" className="m-0">C</Tag>];
      } else {
        otherStatus = [...otherStatus, <Tag key={index} color="green" className="m-0">A</Tag>];
      }
      index += 1;
    }
    setDisplayStatus(otherStatus);
  }, [stage]);

  const generatePostSteps = useCallback(() => {
    let otherStatus: (JSX.Element | Element)[] = [];

    let index = 1;
    while (index < stageCount) {
      otherStatus = [...otherStatus, <Tag key={index} color="grey" className="m-0">P</Tag>];
      index += 1;
    }
  }, [stageCount]);

  const generateRejectedJobStatus = useCallback(() => {
    let rejectedStatus: ReactNode[] = [];
    if (statusStr === 'rejected') {
      rejectedStatus = [...rejectedStatus, <Tag key="rejected" color="red" className="m-0">R</Tag>];
    } else if (user.blocking) {
      rejectedStatus = [...rejectedStatus, <Tag key="userBlocking" color="yellow" className="m-0">P</Tag>];
    } else {
      rejectedStatus = [...rejectedStatus, <Tag key="rejectedStatus" color="grey" className="m-0">P</Tag>];
    }
    setDisplayStatus(rejectedStatus);
  }, [statusStr, user.blocking]);

  const generateOtherJobStatus = useCallback(() => {
    generatePreSteps();
    generateRejectedJobStatus();
    generatePostSteps();
  }, [generatePreSteps, generateRejectedJobStatus, generatePostSteps]);

  useEffect(() => {
    if (archivedAt) {
      setDisplayStatus([displayArchivedStatus()]);
      return;
    }
    if (stage === 0) {
      setDisplayStatus([displayDraftStatus()]);
      return;
    }
    if (statusStr === 'accepted') {
      generateAcceptedJobStatus();
    } else {
      generateOtherJobStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, archivedAt, statusStr]);
  const jobSituationsData = humps(jobSituations || []) as JobSituationDataProps;

  return (
    <>
      <Row>
        <Col xs={24} sm={24} md={24} lg={24} xl={24}>
          <div className="flex flex-wrap items-center gap-1">
            {React.Children.map(displayStatus, (status) => (
              <button
                type="button"
                className="bg-transparent border-0 p-0 cursor-pointer rounded-sm transition-all duration-150 hover:shadow-sm hover:transform hover:-translate-y-px focus:outline-none focus:ring-2 focus:ring-blue-400 active:transform active:scale-95"
                onClick={() => setOpenChangePopOver(true)}
                aria-label={t('job.reviewerModal.open')}
              >
                {status}
              </button>
            ))}
          </div>
        </Col>
      </Row>
      <Modal
        centered
        destroyOnClose
        closable
        maskClosable={false}
        visible={openPopOver}
        width={820}
        title={(
          <div>
            <div>{t('job.reviewerModal.title')}</div>
            <div className="font-normal text-sm text-gray-500">
              {t('job.reviewerModal.subtitle', { id })}
            </div>
          </div>
        )}
        onCancel={() => setOpenChangePopOver(false)}
        footer={(
          <Button onClick={() => setOpenChangePopOver(false)}>
            {t('job.reviewerModal.close')}
          </Button>
        )}
      >
        <JobStatusToast situations={jobSituationsData} loading={loading} />
      </Modal>
    </>
  );
};

JobStatus.defaultProps = {
  jobSituations: [],
};

export default JobStatus;
